// src/markets/HarvestPaceCard.jsx — NASS harvest pace gauges for Market Intelligence.
//
// Renders what src/markets/harvestPace.js decided; it computes no pace of its own.
// Market Intelligence is an OPEN page (any portal user may be granted it), so this
// card reads exactly one table — nass_data, USDA's published state statistics —
// and nothing about any farm. tools/check-nass-harvest-pace.js holds that.
//
// STATEWIDE ONLY. NASS publishes Crop Progress for the whole state and no county
// series exists, so this card does not move with the page's NY / Western NY toggle
// and says so on its face.
//
// The data fetcher is INJECTED (the LrpPushSignalView pattern), so this module
// carries no dependency on App.jsx internals.
import React, { useEffect, useState } from "react";
import { harvestPaceAll, fmtWeekEnding } from "./harvestPace";

const STATS = ["PCT_HARVESTED", "PCT_HARVESTED_GRAIN", "PCT_HARVESTED_SILAGE"];
const INK = "#1a2e1a", MUTE = "#5a6a5a", LINE = "#dde7dd", TRACK = "#e3ece3", FILL = "#2d6a2d", AVG = "#1a1a1a", LAST = "#9aa79a";

const easternToday = () => {
  try { return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }); }
  catch (_) { return new Date().toISOString().slice(0, 10); }
};

// Same geometry as the Market Barometer gauge (a 0-100 semicircle, needle from the
// hub), so the two read as one family. The scale here is % harvested, not a score.
function PaceGauge({ p }) {
  const W = 200, H = 116, cx = 100, cy = 100, r = 78;
  const pt = (v, rad) => { const th = Math.PI * (1 - v / 100); return [cx + rad * Math.cos(th), cy - rad * Math.sin(th)]; };
  const arc = (a, b) => { const [x1, y1] = pt(a, r), [x2, y2] = pt(b, r); return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`; };
  const clamp = (v) => Math.max(0, Math.min(100, v));
  const cur = p.current ? clamp(p.current.value) : null;
  const avg = p.average && p.average.rounded != null ? clamp(p.average.rounded) : null;
  const ly = p.lastYear && !p.lastYear.absent ? clamp(p.lastYear.value) : null;
  const tick = (v, color, w) => { const [x1, y1] = pt(v, r - 11), [x2, y2] = pt(v, r + 11); return <line x1={x1.toFixed(2)} y1={y1.toFixed(2)} x2={x2.toFixed(2)} y2={y2.toFixed(2)} stroke={color} strokeWidth={w} />; };
  const [nx, ny] = cur != null ? pt(cur, r - 14) : [cx, cy];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 220, display: "block", margin: "4px auto 0" }} role="img"
      aria-label={`${p.label}: ${cur != null ? cur + "% harvested" : "no reading"}${avg != null ? `, 5-year average ${avg}%` : ""}`}>
      <path d={arc(0.3, 99.7)} fill="none" stroke={TRACK} strokeWidth="16" />
      {cur != null && cur > 0.5 && <path d={arc(0.3, Math.max(0.6, cur))} fill="none" stroke={FILL} strokeWidth="16" opacity="0.55" />}
      {ly != null && tick(ly, LAST, 2)}
      {avg != null && tick(avg, AVG, 3)}
      {cur != null && <line x1={cx} y1={cy} x2={nx.toFixed(2)} y2={ny.toFixed(2)} stroke={AVG} strokeWidth="3.2" strokeLinecap="round" />}
      <circle cx={cx} cy={cy} r="6" fill={cur != null ? AVG : "#c8c8c8"} />
      <text x="16" y="114" fontSize="9" fill={MUTE}>0%</text>
      <text x="170" y="114" fontSize="9" fill={MUTE}>100%</text>
    </svg>
  );
}

function PaceTile({ p, isMobile }) {
  const big = p.current ? `${p.current.value}%` : "—";
  let sub;
  if (p.state === "no_history" || p.state === "not_started") sub = p.sentence.replace(`${p.label}: `, "");
  else if (p.diff == null) sub = `5-year average not available — ${p.average.reason}`;
  else if (p.pace === "even") sub = `Even with the 5-year average (${p.average.rounded}%)`;
  else sub = `${Math.abs(p.diff)} pt${Math.abs(p.diff) === 1 ? "" : "s"} ${p.pace === "ahead" ? "ahead of" : "behind"} the 5-year average (${p.average.rounded}%)`;
  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, padding: 12, textAlign: "center", minWidth: 0 }}>
      <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 800, color: INK }}>{p.label}</div>
      <PaceGauge p={p} />
      <div style={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1.1 }}>{big}</div>
      <div style={{ fontSize: 11.5, color: MUTE }}>{p.current ? "harvested" : ""}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, marginTop: 4, minHeight: 16 }}>{sub}</div>
      {p.current && (
        <div style={{ fontSize: 11.5, color: MUTE, marginTop: 4, lineHeight: 1.35 }}>
          {p.lastYear.absent ? `Last year: ${p.lastYear.reason}.` : `Last year ${p.lastYear.value}%.`} Week ending {fmtWeekEnding(p.current.weekEnding)}.
          {p.state === "complete" && " Harvest complete."}
        </div>
      )}
      {p.missingWeeks && p.missingWeeks.length > 0 && (
        <div style={{ fontSize: 11.5, color: "#8a5a00", marginTop: 4 }}>
          NASS has not published week ending {fmtWeekEnding(p.missingWeeks[p.missingWeeks.length - 1])} — showing the last week it has.
        </div>
      )}
    </div>
  );
}

export default function HarvestPaceCard({ isMobile = false, sbFetch }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.resolve(sbFetch("nass_data", {
      select: "crop,year,week,stat_type,value",
      filters: ["state=eq.NY", "county=is.null", `stat_type=in.(${STATS.join(",")})`],
      order: "year,week,id.asc",
    })).then((r) => { if (alive) setRows(r || []); })
      .catch((e) => { if (alive) setErr(e && e.message ? e.message : "load failed"); });
    return () => { alive = false; };
  }, [sbFetch]);

  const today = easternToday();
  const paces = rows ? harvestPaceAll(rows, today) : [];
  const bridged = [...new Set(paces.flatMap((p) => (p.average && p.average.bridged ? p.average.bridged.map((b) => b.year) : [])))];

  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, padding: isMobile ? 12 : 16, marginBottom: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>Harvest pace — New York, statewide</div>
        <div style={{ fontSize: 11.5, color: MUTE }}>USDA NASS Crop Progress · weekly</div>
      </div>
      <div style={{ fontSize: 12, color: MUTE, marginTop: 3 }}>
        NASS reports harvest progress for the whole state only, so this does not change with the region selector.
        Needle = this week · dark mark = 5-year average · light mark = last year.
      </div>
      {err && <div style={{ fontSize: 12.5, color: "#a33", marginTop: 10 }}>Couldn't load NASS harvest progress.</div>}
      {!err && !rows && <div style={{ fontSize: 12.5, color: MUTE, marginTop: 10 }}>Loading harvest progress…</div>}
      {!err && rows && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
          {paces.map((p) => <PaceTile key={p.key} p={p} isMobile={isMobile} />)}
        </div>
      )}
      {!err && rows && (
        <div style={{ fontSize: 11, color: MUTE, marginTop: 8, lineHeight: 1.4 }}>
          The 5-year average reads each of the last five years on the same calendar date, between its weekly reports — NASS's own method.
          Corn for grain and corn for silage are separate NASS series and are never combined.
          {bridged.length > 0 && ` NASS published no New York reports for part of ${bridged.join(", ")}; in the average that year is read across the gap, as NASS's own published average does.`}
        </div>
      )}
    </div>
  );
}
